// The builder's Assist tab (mockup 07): the help level; in Semi-auto what the app may add, the
// target score, where the logic stalls and the suggestion cards, with Find break-in, Complete
// and Tidy; in Automatic the same choices plus symmetry, and the candidates to pick from.
import type { AutoCandidate, BuildReport, HelpLevel, Suggestion } from '@zinc/shared';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ADDABLE, allowedChips } from '../../lib/builder/palette';
import { ScoreChip } from '../ScoreChip';
import { Thumbnail } from '../Thumbnail';
import { TickIcon } from '../icons';

const LEVELS: { value: HelpLevel; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'semi', label: 'Semi-auto' },
  { value: 'automatic', label: 'Automatic' },
];

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

export function letterOf(i: number): string {
  return LETTERS[i] ?? String(i + 1);
}

function LevelSwitch({ value, onChange }: { value: HelpLevel; onChange: (l: HelpLevel) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Help level"
      className="grid grid-cols-3 gap-[3px] rounded-btn bg-segment p-[3px]"
    >
      {LEVELS.map((l) => {
        const on = l.value === value;
        return (
          <button
            key={l.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(l.value)}
            className={`h-9 rounded-[8px] border-0 text-[13px] ${on ? 'bg-surface font-bold text-ink shadow-[0_1px_2px_rgba(31,30,28,0.14)]' : 'bg-transparent font-medium text-secondary'}`}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

/** The app's allowed types as chips, and "+ Add type" with a menu of the rest. */
function MayAdd({
  title,
  allowed,
  onChange,
}: {
  title: string;
  allowed: string[];
  onChange: (types: string[]) => void;
}) {
  const chips = allowedChips(allowed);
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    setAt({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 272) });
  }, [open]);
  const toggle = (types: string[], on: boolean) =>
    onChange(on ? [...new Set([...allowed, ...types])] : allowed.filter((t) => !types.includes(t)));
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[12px] font-bold text-muted">{title}</span>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            aria-label={`${c.name}: the app may add it (tap to take off)`}
            onClick={() => toggle(c.types, false)}
            className="flex h-7 items-center rounded-full border border-chip-line bg-accent-tint px-2.5 text-[12px] font-semibold text-accent-dark"
          >
            {c.name}
          </button>
        ))}
        <button
          ref={button}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="h-7 rounded-full border border-dashed border-dash bg-surface px-2.5 text-[12px] font-semibold text-subtle"
        >
          + Add type
        </button>
      </div>
      {open && at && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            aria-label="Types the app may add"
            className="fixed z-40 flex max-h-[min(420px,calc(100dvh-140px))] w-[260px] flex-col overflow-y-auto rounded-[16px] border border-line bg-surface p-2 shadow-[0_8px_24px_rgba(31,30,28,0.16)]"
            style={{ top: at.top, left: at.left }}
          >
            {ADDABLE.map((a) => {
              const on = a.types.every((t) => allowed.includes(t));
              return (
                <button
                  key={a.key}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={on}
                  onClick={() => toggle(a.types, !on)}
                  className="flex min-h-10 w-full items-center gap-2 rounded-btn border-0 bg-transparent px-3 text-left text-[14px] font-medium text-ink hover:bg-bg"
                >
                  <span className="flex w-[18px] shrink-0 text-accent">
                    {on ? <TickIcon /> : null}
                  </span>
                  {a.name}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const fmt = (x: number) => x.toFixed(1);

/** The target score on the green-to-red scale; drag on the bar to move the nearer end. */
export function TargetScore({
  value,
  onChange,
}: {
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const end = useRef<0 | 1>(0);
  const [lo, hi] = value;
  const at = (clientX: number) => {
    const r = bar.current!.getBoundingClientRect();
    return Math.round(Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * 20) / 2;
  };
  const move = (v: number) => {
    const next: [number, number] =
      end.current === 0 ? [Math.min(v, hi), hi] : [lo, Math.max(v, lo)];
    if (next[0] !== lo || next[1] !== hi) onChange(next);
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between text-[12px] font-bold text-muted">
        <span>Target score</span>
        <span className="text-ink">
          {fmt(lo)}–{fmt(hi)}
        </span>
      </div>
      <div
        ref={bar}
        className="relative h-2.5 touch-none rounded-[5px]"
        style={{ background: 'linear-gradient(90deg, #6CC287 0%, #F2CE5B 50%, #E0645A 100%)' }}
        onPointerDown={(e) => {
          const v = at(e.clientX);
          end.current = Math.abs(v - lo) <= Math.abs(v - hi) && !(v > hi) ? 0 : 1;
          e.currentTarget.setPointerCapture(e.pointerId);
          move(v);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) move(at(e.clientX));
        }}
      >
        <span
          aria-hidden="true"
          className="absolute -top-1 box-border h-3.5 rounded-[4px] border-2 border-ink bg-[rgba(255,255,255,0.35)]"
          style={{ left: `${lo * 10}%`, width: `${Math.max(2, (hi - lo) * 10)}%` }}
        />
      </div>
      <div className="flex justify-between text-[11px] text-muted">
        <span>0.0</span>
        <span>10.0</span>
      </div>
      <div className="sr-only">
        <input
          type="range"
          aria-label="Lowest target score"
          min={0}
          max={10}
          step={0.5}
          value={lo}
          onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
        />
        <input
          type="range"
          aria-label="Highest target score"
          min={0}
          max={10}
          step={0.5}
          value={hi}
          onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
        />
      </div>
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-btn bg-note-bg px-3 py-2.5 text-[13px] text-note-ink"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="mt-px shrink-0 text-note-icon"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </svg>
      <span>{children}</span>
    </div>
  );
}

function metaOf(s: Suggestion): string {
  const parts: string[] = [];
  if (s.scoreAfter !== null) {
    parts.push(
      s.scoreBefore !== null
        ? `Score ${fmt(s.scoreBefore)} → ${fmt(s.scoreAfter)}`
        : `Score ${fmt(s.scoreAfter)} with it`,
    );
  }
  parts.push(
    s.moreSteps > 0
      ? `unlocks ${s.moreSteps} more step${s.moreSteps === 1 ? '' : 's'}`
      : `removes ${s.removes} candidate${s.removes === 1 ? '' : 's'}`,
  );
  if (s.unique && s.scoreAfter === null) parts.push('one solution');
  if (s.target === 'above') parts.push('above target');
  if (s.target === 'below') parts.push('below target');
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const small =
  'h-8 rounded-[8px] border border-line bg-surface px-3 text-[13px] font-medium text-ink';

function SuggestionCard({
  s,
  letter,
  onAccept,
  onEdit,
  onReject,
}: {
  s: Suggestion;
  letter: string;
  onAccept: () => void;
  onEdit: () => void;
  onReject: () => void;
}) {
  return (
    <div
      aria-label={`Suggestion ${letter}`}
      className="flex flex-col gap-1.5 rounded-[12px] border border-line px-3 py-2.5"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[10px] bg-accent text-[11px] font-bold text-white">
          {letter}
        </span>
        <span className="flex-1 text-[14px] font-semibold">{s.label}</span>
      </div>
      <span className="text-[12px] text-muted">{metaOf(s)}</span>
      {s.opens && <span className="text-[12px] text-muted">Opens with: {s.opens}</span>}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={onAccept}
          className="h-8 rounded-[8px] border-0 bg-accent px-3 text-[13px] font-semibold text-white"
        >
          Accept
        </button>
        {s.entry && (
          <button type="button" onClick={onEdit} className={small}>
            Edit
          </button>
        )}
        <button type="button" onClick={onReject} className={small}>
          Reject
        </button>
      </div>
    </div>
  );
}

export interface AssistState {
  suggestions: Suggestion[];
  note: string | null;
  busy: string | null;
  /** The last tool's outcome ("Tidy removed 2 pieces."). */
  message: string | null;
  /** A worker job running: Complete or Automatic, with its progress. */
  progress: number | null;
}

export interface AssistPanelProps {
  level: HelpLevel;
  onLevel: (l: HelpLevel) => void;
  allowed: string[];
  onAllowed: (types: string[]) => void;
  target: [number, number];
  onTarget: (t: [number, number]) => void;
  report: BuildReport | null;
  /** The puzzle has pieces: where the logic stalls is worth saying (not on an empty grid). */
  showStall: boolean;
  locked: number;
  state: AssistState;
  onAccept: (s: Suggestion) => void;
  onEdit: (s: Suggestion) => void;
  onReject: (s: Suggestion) => void;
  onSuggest: () => void;
  onBreakIn: () => void;
  onComplete: () => void;
  onTidy: () => void;
  // Automatic
  symmetry: boolean;
  onSymmetry: (on: boolean) => void;
  onGenerate: () => void;
  candidates: AutoCandidate[];
  onUse: (c: AutoCandidate) => void;
}

function stallText(report: BuildReport | null, locked: number, level: HelpLevel): string | null {
  if (!report?.stall) return null;
  const where = `The logic stalls at step ${report.stall.step} in ${report.stall.house}`;
  if (level !== 'semi') return `${where}.`;
  const own = locked > 0 ? `Your ${locked} piece${locked === 1 ? ' is' : 's are'} locked. ` : '';
  return `${own}${where}; these would move it forward.`;
}

export function AssistPanel(p: AssistPanelProps) {
  const { report, state } = p;
  const stall = p.showStall ? stallText(report, p.locked, p.level) : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3.5">
      <LevelSwitch value={p.level} onChange={p.onLevel} />
      {p.level === 'manual' && (
        <>
          {stall && <Note>{stall}</Note>}
          {report?.score && (
            <p className="m-0 text-[13px] text-muted">
              Hardest step: {report.score.hardest.technique.replace(/-/g, ' ')}
              {report.score.hardest.rule ? ` (${report.score.hardest.rule})` : ''}, cost{' '}
              {fmt(report.score.hardest.cost)}.
            </p>
          )}
          <p className="m-0 text-[13px] text-muted">
            You place everything. Semi-auto suggests clues where the logic stalls; Automatic makes
            whole puzzles to start from.
          </p>
        </>
      )}
      {p.level === 'semi' && (
        <>
          <MayAdd title="The app may add" allowed={p.allowed} onChange={p.onAllowed} />
          <TargetScore value={p.target} onChange={p.onTarget} />
          {stall && <Note>{stall}</Note>}
          {state.note && <p className="m-0 text-[13px] text-muted">{state.note}</p>}
          {state.busy && (
            <p role="status" className="m-0 text-[13px] text-muted">
              {state.busy}
            </p>
          )}
          {state.message && (
            <p role="status" className="m-0 text-[13px] text-ink">
              {state.message}
            </p>
          )}
          {state.progress !== null && <Progress value={state.progress} label="Completing" />}
          <div className="flex flex-col gap-2">
            {state.suggestions.map((s, i) => (
              <SuggestionCard
                key={`${s.label}-${i}`}
                s={s}
                letter={letterOf(i)}
                onAccept={() => p.onAccept(s)}
                onEdit={() => p.onEdit(s)}
                onReject={() => p.onReject(s)}
              />
            ))}
          </div>
          {report?.stall && state.suggestions.length === 0 && !state.busy && (
            <button type="button" onClick={p.onSuggest} className={`${small} self-start`}>
              Suggest next
            </button>
          )}
          <div className="mt-auto grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={p.onBreakIn}
              disabled={state.busy !== null}
              className="h-10 rounded-btn border border-line bg-surface text-[13px] font-semibold text-ink disabled:opacity-45"
            >
              Find break-in
            </button>
            <button
              type="button"
              onClick={p.onComplete}
              disabled={state.busy !== null || state.progress !== null}
              className="h-10 rounded-btn border-0 bg-ink text-[13px] font-semibold text-white disabled:opacity-45"
            >
              Complete
            </button>
            <button
              type="button"
              onClick={p.onTidy}
              disabled={state.busy !== null}
              className="h-10 rounded-btn border border-line bg-surface text-[13px] font-semibold text-ink disabled:opacity-45"
            >
              Tidy
            </button>
          </div>
        </>
      )}
      {p.level === 'automatic' && (
        <>
          <MayAdd title="Constraint mix" allowed={p.allowed} onChange={p.onAllowed} />
          <TargetScore value={p.target} onChange={p.onTarget} />
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={p.symmetry}
              onChange={(e) => p.onSymmetry(e.target.checked)}
            />
            Symmetric givens
          </label>
          {state.message && (
            <p role="status" className="m-0 text-[13px] text-ink">
              {state.message}
            </p>
          )}
          {state.progress !== null ? (
            <Progress value={state.progress} label="Making candidates" />
          ) : (
            <button
              type="button"
              onClick={p.onGenerate}
              className="h-10 rounded-btn border-0 bg-ink text-[13px] font-semibold text-white"
            >
              {p.candidates.length ? 'Make new candidates' : 'Make candidates'}
            </button>
          )}
          <div className="flex flex-col gap-2">
            {p.candidates.map((c, i) => (
              <div
                key={i}
                aria-label={`Candidate ${letterOf(i)}`}
                className="flex items-center gap-3 rounded-[12px] border border-line px-3 py-2.5"
              >
                <Thumbnail doc={c.doc} size={64} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-[14px] font-semibold">Candidate {letterOf(i)}</span>
                  <span className="text-[12px] text-muted">
                    {c.doc.givens.length} given{c.doc.givens.length === 1 ? '' : 's'} ·{' '}
                    {c.doc.constraints.length} clue{c.doc.constraints.length === 1 ? '' : 's'}
                    {c.inRange ? '' : ' · outside the target'}
                  </span>
                </div>
                <ScoreChip score={c.score.score} />
                <button
                  type="button"
                  onClick={() => p.onUse(c)}
                  className="h-8 rounded-[8px] border-0 bg-accent px-3 text-[13px] font-semibold text-white"
                >
                  Use
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Progress({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] text-muted">{label}…</span>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value * 100)}
        className="h-2 overflow-hidden rounded-full bg-segment"
      >
        <span
          className="block h-full rounded-full bg-accent transition-[width] duration-700"
          style={{ width: `${Math.max(4, value * 100)}%` }}
        />
      </div>
    </div>
  );
}
